import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import { fine } from '../../utils/format';
import { Drawer } from '../../components/ui/Form';
import { Loading } from '../../components/ui';

interface StockItem {
  id: string;
  item_code: string;
  product_name: string;
  image_data_uri?: string | null;
  location_name?: string | null;
  purity_code: string;
  gross_wt: string;
  net_wt: string;
  fine_wt: string;
  branch_id: string;
}

/**
 * Pick a piece off the shelf when there is no scanner to hand.
 *
 * A shop without a barcode gun — or with a tag that will not read — still has
 * to be able to sell, and hunting for a piece by its code in a flat list is
 * slower than typing part of the name. So this searches on both, shows only
 * what is actually in stock at this counter, and adds on a click without
 * closing, because a bill is usually more than one piece.
 */
export function ItemPicker({
  branchId,
  alreadyOn,
  onPick,
  onClose,
}: {
  branchId: string;
  alreadyOn: string[];
  onPick: (item: StockItem) => void;
  onClose: () => void;
}) {
  const [term, setTerm] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 20);
  }, []);

  const { data, isLoading } = useQuery({
    queryKey: ['pos-pick', branchId, term],
    queryFn: () =>
      api.get<{ data: StockItem[] }>(
        `/items?status=IN_STOCK&limit=50${branchId ? `&branchId=${branchId}` : ''}${
          term ? `&q=${encodeURIComponent(term)}` : ''
        }`,
      ),
  });

  const rows = data?.data ?? [];

  return (
    <Drawer title="Pick from stock" subtitle="Search by tag or name; tap to add to the bill." onClose={onClose}>
      <input
        ref={inputRef}
        className="field mb-3"
        placeholder="Tag, barcode or name — ring, BNG-0001…"
        value={term}
        onChange={(e) => setTerm(e.target.value)}
      />

      {isLoading ? <Loading rows={4} /> : null}

      {!isLoading && rows.length === 0 ? (
        <p className="rounded-md border border-silver-100 bg-surface px-4 py-6 text-center text-sm text-ink-soft">
          {term ? 'Nothing in stock matches that.' : 'Nothing in stock at this counter.'}
        </p>
      ) : null}

      <div className="grid grid-cols-2 gap-2">
        {rows.map((item) => {
          const on = alreadyOn.includes(item.id);
          return (
            <button
              key={item.id}
              className={`flex flex-col overflow-hidden rounded-lg border text-left transition-all ${
                on
                  ? 'cursor-default border-silver-100 opacity-50'
                  : 'border-silver-100 bg-surface hover:border-gold-500 hover:shadow-card'
              }`}
              disabled={on}
              onClick={() => onPick(item)}
            >
              <div className="grid h-24 w-full place-items-center bg-silver-50">
                {item.image_data_uri ? (
                  <img src={item.image_data_uri} alt="" className="h-full w-full object-cover" />
                ) : (
                  <span className="text-2xs text-ink-faint">{item.purity_code}</span>
                )}
              </div>
              <div className="p-2">
                <p className="num text-sm font-medium">{item.item_code}</p>
                <p className="truncate text-2xs text-ink-soft">{item.product_name}</p>
                <p className="mt-0.5 flex items-center justify-between text-2xs">
                  <span className="chip-mute">{item.purity_code}</span>
                  <span className="num text-ink-soft">{fine(item.fine_wt)}</span>
                </p>
                {item.location_name ? <p className="mt-0.5 truncate text-2xs text-ink-faint">{item.location_name}</p> : null}
                {on ? <p className="mt-1 text-2xs text-gold-700">on bill</p> : null}
              </div>
            </button>
          );
        })}
      </div>
    </Drawer>
  );
}
